import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetApiTenantsTenantIdRuntimeMediaAudioMetadata } from "@/lib/api/generated/runtime-media-audio/runtime-media-audio";
import {
  useGetApiTenantsTenantId,
  usePatchApiTenantsTenantId,
} from "@/lib/api/generated/tenants/tenants";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { toastMutationError } from "@/lib/utils/formatting";
import {
  buildRuntimePoliciesPatch,
  formatProviderSelectionLabel,
  readRuntimeMediaAudioPolicy,
  resolveSuggestedAudioModel,
  type RuntimeMediaAudioPolicy,
} from "./runtime-media-audio-utils";

export function RuntimeMediaAudioCard() {
  const { tenantId } = useTenantContext();
  const { data: tenant, refetch: refetchTenant } = useGetApiTenantsTenantId(tenantId ?? "", {
    query: { enabled: Boolean(tenantId) },
  });
  const { data: metadata } = useGetApiTenantsTenantIdRuntimeMediaAudioMetadata(tenantId ?? "", {
    query: { enabled: Boolean(tenantId) },
  });
  const patchTenant = usePatchApiTenantsTenantId();

  const existingPolicies = (tenant?.runtimePolicies ?? null) as Record<string, unknown> | null;
  const [draft, setDraft] = useState<RuntimeMediaAudioPolicy>(() =>
    readRuntimeMediaAudioPolicy(existingPolicies, metadata),
  );

  useEffect(() => {
    setDraft(readRuntimeMediaAudioPolicy(existingPolicies, metadata));
  }, [existingPolicies, metadata]);

  const selectableProviders = useMemo(
    () => metadata?.activeAudioCapableProviderIds ?? [],
    [metadata],
  );
  const providerSelectionAuto = metadata?.providerSelectionAuto ?? "auto";
  const suggestedModel = resolveSuggestedAudioModel(draft.providerSelection, metadata);
  const hasAudioProvider =
    draft.providerSelection === providerSelectionAuto
      ? selectableProviders.length > 0
      : selectableProviders.includes(draft.providerSelection);

  const onSave = async () => {
    if (!tenantId || !tenant) {
      return;
    }
    if (draft.enabled && !hasAudioProvider) {
      toast.error("Connect a speech-to-text AI provider before enabling voice transcription.");
      return;
    }

    try {
      await patchTenant.mutateAsync({
        tenantId,
        data: {
          tenantId,
          name: tenant.name,
          slug: tenant.slug ?? undefined,
          status: tenant.status ?? undefined,
          runtimePolicies: buildRuntimePoliciesPatch(existingPolicies, draft, metadata),
        },
      });
      await refetchTenant();
      toast.success("Voice transcription policy saved");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to save voice transcription policy"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voice Transcription (STT)</CardTitle>
        <CardDescription>
          Configure how inbound voice notes are transcribed before the agent sees them. Policy is
          stored in tenant runtime settings and projected into the workspace runtime bootstrap.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) =>
              setDraft((current) => ({ ...current, enabled: event.target.checked }))
            }
          />
          <span style={{ color: "var(--text-strong)" }}>Enable audio transcription</span>
        </label>

        <FormField label="Provider">
          <select
            className="form-input"
            value={draft.providerSelection}
            onChange={(event) => {
              const providerSelection = event.target.value;
              setDraft((current) => ({
                ...current,
                providerSelection,
                model:
                  providerSelection === providerSelectionAuto
                    ? null
                    : resolveSuggestedAudioModel(providerSelection, metadata),
              }));
            }}
            disabled={!draft.enabled || !metadata}
          >
            <option value={providerSelectionAuto}>
              {formatProviderSelectionLabel(providerSelectionAuto, metadata)}
            </option>
            {selectableProviders.map((providerId) => (
              <option key={providerId} value={providerId}>
                {formatProviderSelectionLabel(providerId, metadata)}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          label="Model"
          hint={
            draft.providerSelection === providerSelectionAuto
              ? "Model overrides apply only when a provider is selected manually."
              : suggestedModel
                ? `Suggested default: ${suggestedModel}`
                : "Leave blank to use the provider plugin default."
          }
        >
          <input
            className="form-input font-mono"
            value={draft.providerSelection === providerSelectionAuto ? "" : (draft.model ?? "")}
            placeholder={
              draft.providerSelection === providerSelectionAuto
                ? "Provider default at bootstrap"
                : (suggestedModel ?? "Provider default")
            }
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                model: event.target.value.trim() ? event.target.value.trim() : null,
              }))
            }
            disabled={!draft.enabled || draft.providerSelection === providerSelectionAuto}
          />
        </FormField>

        {draft.enabled && !hasAudioProvider ? (
          <p className="text-xs" style={{ color: "var(--danger)" }}>
            No active speech-to-text AI provider connection is configured for this tenant.
          </p>
        ) : null}

        <div className="flex items-center gap-3 pt-1">
          <Button type="button" size="sm" onClick={onSave} disabled={patchTenant.isPending}>
            {patchTenant.isPending ? "Saving..." : "Save STT Policy"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {hint ? (
        <span className="text-[10px]" style={{ color: "var(--muted-foreground)" }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
